import { TBizIcon } from '../../Components/Mini/BizIcon';
import { addMonths, getQuarter } from '../../Utils/dateUtils';

export type DashboardFilter = {
	period: string | null;
	unitId?: number;
	roleId?: number;
	eventCategoryId?: number;
	custom: Record<string, string | number | null>;
};

export type DashboardCustomFilter = {
	id: string;
	type: 'dropdown';
	label: string;
	noSelectText?: string;
	options: { Id: number | string; Name: string }[];
};

export type DashboardWidgetConfig = {
	id: string;
	componentType: string; // Key to identify which component to load
	width: 'full' | 'half' | 'third';
	title?: string;
	icon?: TBizIcon;
	description?: string;
	translationKey?: string;
	config?: Record<string, unknown>; // Widget-specific configuration
};

export type DashboardConfig = {
	id: string;
	name: string;
	widgets: DashboardWidgetConfig[];
	filters: {
		periods: { Id: DashboardPeriod; Name: string; TranslationKey?: string }[];
		unit?: boolean;
		role?: boolean;
		eventCategories?: boolean;
		custom?: DashboardCustomFilter[];
	};
	defaultFilters?: DashboardFilter;
};

export type DashboardWidgetProps = Omit<DashboardWidgetConfig, 'width'> & {
	filters: DashboardFilter;
};

export type StatCountEntity = {
	label: string;
	count: number;
};

export type DashboardPeriod =
	| 'CURRENT_MONTH'
	| 'LAST_MONTH'
	| 'CURRENT_QUARTER'
	| 'LAST_QUARTER'
	| 'CURRENT_HALF_YEAR'
	| 'LAST_HALF_YEAR'
	| 'CURRENT_YEAR'
	| 'LAST_YEAR';

// Helper function to get date range for a given period
export function getDateRangeForPeriod(period: DashboardPeriod): { from: Date; to: Date } {
	const now = new Date();
	let from: Date;
	let to: Date = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Default to today
	switch (period) {
		case 'CURRENT_MONTH':
			from = new Date(now.getFullYear(), now.getMonth(), 1);
			break;
		case 'LAST_MONTH': {
			const date = addMonths(now, -1);
			from = new Date(date.getFullYear(), date.getMonth(), 1);
			to = new Date(now.getFullYear(), now.getMonth(), 0); // Last day of last month
			break;
		}
		case 'CURRENT_QUARTER': {
			const currentQuarter = getQuarter(now);
			//console.log('currentQuarter:', currentQuarter);

			from = new Date(now.getFullYear(), (currentQuarter - 1) * 3, 1);
			break;
		}
		case 'LAST_QUARTER': {
			const date = addMonths(now, -3);
			const lastQuarter = getQuarter(date);
			from = new Date(date.getFullYear(), (lastQuarter - 1) * 3, 1);
			to = new Date(date.getFullYear(), lastQuarter * 3, 0); // Last day of last quarter
			break;
		}
		case 'CURRENT_HALF_YEAR': {
			const currentHalf = now.getMonth() < 6 ? 0 : 6;
			from = new Date(now.getFullYear(), currentHalf, 1);
			break;
		}
		case 'LAST_HALF_YEAR': {
			const date = addMonths(now, -6);
			const lastHalf = date.getMonth() < 6 ? 0 : 6;
			from = new Date(date.getFullYear(), lastHalf, 1);
			to = new Date(date.getFullYear(), lastHalf + 6, 0); // Last day of last half year
			break;
		}
		case 'CURRENT_YEAR':
			from = new Date(now.getFullYear(), 0, 1);
			break;
		case 'LAST_YEAR':
			from = new Date(now.getFullYear() - 1, 0, 1);
			to = new Date(now.getFullYear() - 1, 11, 31);
			break;
		default:
			from = new Date(now.getFullYear(), now.getMonth(), 1);
	}
	return { from, to };
}
